/*
  Carolina Pet: optional pet breed type-ahead picker.
  Upgrades each native <select> rendered by snippets/breed-picker-field.liquid into an accessible
  combobox (WAI-ARIA 1.2 pattern). The field is optional. Only breeds from the list are submitted,
  as a line item property ("Dog Breed" or "Cat Breed") through a hidden input tied to the product form.
  <pet-type-toggle> swaps between the dog and cat lists on products that suit both.
*/
if (!customElements.get('dog-breed-picker')) {
  customElements.define(
    'dog-breed-picker',
    class DogBreedPicker extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        const select = this.querySelector('select');
        if (!select) return;
        this.dataset.ready = 'true';

        this.breeds = Array.from(select.options)
          .map((option) => option.value)
          .filter(Boolean);
        this.searchIndex = this.breeds.map((breed) => DogBreedPicker.normalize(breed));
        this.otherOption = this.breeds.find((breed) => /^other/i.test(breed)) || null;
        this.invalidMessage = this.dataset.invalidMessage || 'Choose a breed from the list, or leave this blank.';
        this.noMatchMessage = this.dataset.noMatchMessage || 'No matches.';
        this.form = this.resolveForm(select);
        // form.id would return the form's hidden input named "id", so read the attribute
        this.formId = this.form ? this.form.getAttribute('id') : this.dataset.formId;
        this.value = '';
        this.activeIndex = -1;
        this.results = [];

        this.build(select);
        this.bindEvents();
        this.setActive(!this.hasAttribute('data-inactive'));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
      }

      static normalize(text) {
        return String(text)
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[’']/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      resolveForm(select) {
        const byId = this.dataset.formId && document.getElementById(this.dataset.formId);
        if (byId && byId.tagName === 'FORM') return byId;
        if (select.form) return select.form;
        const scope = this.closest('product-info') || this.closest('[id^="shopify-section"]') || document;
        return scope.querySelector('form.main-product-form, form[is="product-form"]');
      }

      build(select) {
        const uid = select.id || 'Breed-' + Math.random().toString(36).slice(2);
        const listboxId = uid + '-listbox';
        const errorId = uid + '-error';
        const fallback = this.querySelector('.dbp__fallback');

        const wrapper = document.createElement('div');
        wrapper.className = 'dbp__control';

        const input = document.createElement('input');
        input.type = 'text';
        input.id = uid;
        input.className = 'dbp__input form-control';
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-controls', listboxId);
        input.setAttribute('aria-describedby', errorId);
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('autocapitalize', 'off');
        input.setAttribute('spellcheck', 'false');
        input.placeholder = this.dataset.placeholder || 'Start typing a breed';
        // The visible text box only searches. It never submits; the hidden input below carries the value.
        if (this.formId) input.setAttribute('form', this.formId + '-search-only');

        const listbox = document.createElement('ul');
        listbox.id = listboxId;
        listbox.className = 'dbp__listbox list-unstyled';
        listbox.setAttribute('role', 'listbox');
        listbox.setAttribute('aria-label', this.dataset.listLabel || 'Breeds');
        listbox.hidden = true;

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + (this.dataset.property || 'Dog Breed') + ']';
        hidden.value = '';
        if (this.formId) hidden.setAttribute('form', this.formId);

        const error = document.createElement('p');
        error.id = errorId;
        error.className = 'dbp__error';
        error.setAttribute('aria-live', 'polite');
        error.hidden = true;

        const status = document.createElement('p');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');

        wrapper.append(input, listbox);
        if (fallback) fallback.remove();
        this.append(wrapper, hidden, error, status);

        this.input = input;
        this.listbox = listbox;
        this.hiddenInput = hidden;
        this.error = error;
        this.status = status;
      }

      bindEvents() {
        this.input.addEventListener('input', () => {
          this.setValue('', { keepText: true });
          this.hideError();
          this.filter(this.input.value);
          this.open();
        });
        this.input.addEventListener('focus', () => {
          this.filter(this.value ? '' : this.input.value);
          this.open();
        });
        this.input.addEventListener('keydown', (event) => this.onKeydown(event));
        this.input.addEventListener('blur', () => {
          setTimeout(() => {
            if (this.contains(document.activeElement)) return;
            this.commitTypedText();
            this.close();
          }, 120);
        });

        this.listbox.addEventListener('mousedown', (event) => event.preventDefault());
        this.listbox.addEventListener('click', (event) => {
          const option = event.target.closest('[role="option"]');
          if (!option) return;
          this.choose(option.dataset.value);
        });

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        document.addEventListener('click', this.onDocumentClick, true);
      }

      onKeydown(event) {
        const open = !this.listbox.hidden;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!open) {
              this.filter(this.value ? '' : this.input.value);
              this.open();
            }
            this.moveActive(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (!open) {
              this.filter(this.value ? '' : this.input.value);
              this.open();
            }
            this.moveActive(-1);
            break;
          case 'Enter':
            // Enter picks a breed; it never submits the product form from this field.
            event.preventDefault();
            if (open && this.activeIndex > -1 && this.results[this.activeIndex]) {
              this.choose(this.results[this.activeIndex]);
            } else if (open && this.results.length === 1) {
              this.choose(this.results[0]);
            } else {
              this.commitTypedText();
              this.close();
            }
            break;
          case 'Escape':
            if (open) {
              event.preventDefault();
              this.close();
            } else if (!this.value) {
              this.input.value = '';
              this.hideError();
            }
            break;
          case 'Tab':
            if (open && this.activeIndex > -1 && this.results[this.activeIndex]) {
              this.choose(this.results[this.activeIndex]);
            }
            break;
        }
      }

      filter(query) {
        const q = DogBreedPicker.normalize(query);
        let results;
        if (!q) {
          results = this.breeds.slice();
        } else {
          const starts = [];
          const wordStarts = [];
          const contains = [];
          this.searchIndex.forEach((text, index) => {
            const breed = this.breeds[index];
            if (text.startsWith(q)) starts.push(breed);
            else if ((' ' + text).includes(' ' + q)) wordStarts.push(breed);
            else if (text.includes(q)) contains.push(breed);
          });
          results = starts.concat(wordStarts, contains);
        }
        if (q && this.otherOption && !results.includes(this.otherOption)) results.push(this.otherOption);
        this.results = results;
        this.activeIndex = -1;
        this.render(results.length === 1 && this.otherOption && q && results[0] === this.otherOption);
      }

      render(noMatch) {
        const fragment = document.createDocumentFragment();
        if (noMatch) {
          const note = document.createElement('li');
          note.className = 'dbp__note';
          note.setAttribute('aria-hidden', 'true');
          note.textContent = this.noMatchMessage;
          fragment.append(note);
        }
        this.results.forEach((breed, index) => {
          const option = document.createElement('li');
          option.id = this.listbox.id + '-' + index;
          option.className = 'dbp__option';
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', breed === this.value ? 'true' : 'false');
          option.dataset.value = breed;
          option.textContent = breed;
          fragment.append(option);
        });
        this.listbox.replaceChildren(fragment);
        this.input.removeAttribute('aria-activedescendant');
        const count = this.results.length;
        this.status.textContent = noMatch
          ? this.noMatchMessage
          : count + (count === 1 ? ' breed available' : ' breeds available');
      }

      moveActive(step) {
        if (!this.results.length) return;
        const max = this.results.length - 1;
        let next = this.activeIndex + step;
        if (next < 0) next = max;
        if (next > max) next = 0;
        this.activeIndex = next;
        this.listbox.querySelectorAll('[role="option"]').forEach((option, index) => {
          option.classList.toggle('is-active', index === next);
        });
        const active = this.listbox.querySelector('#' + CSS.escape(this.listbox.id + '-' + next));
        if (active) {
          this.input.setAttribute('aria-activedescendant', active.id);
          active.scrollIntoView({ block: 'nearest' });
        }
      }

      open() {
        if (!this.listbox.hidden) return;
        this.listbox.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.listbox.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
      }

      choose(breed) {
        this.setValue(breed);
        this.close();
        this.hideError();
      }

      commitTypedText() {
        if (this.value) {
          this.input.value = this.value;
          return;
        }
        const typed = DogBreedPicker.normalize(this.input.value);
        if (!typed) {
          this.hideError();
          return;
        }
        const index = this.searchIndex.indexOf(typed);
        if (index > -1) {
          this.choose(this.breeds[index]);
        } else {
          // Optional field: nothing is saved, but tell the shopper why.
          this.showError(this.invalidMessage);
        }
      }

      setValue(breed, options = {}) {
        this.value = breed || '';
        this.hiddenInput.value = this.value;
        this.syncSubmit();
        if (!options.keepText) this.input.value = this.value;
        this.dispatchEvent(new CustomEvent('dog-breed:change', { bubbles: true, detail: { value: this.value } }));
      }

      setActive(active) {
        this.active = active;
        this.hidden = !active;
        this.toggleAttribute('data-inactive', !active);
        this.syncSubmit();
        if (!active && this.input) {
          this.close();
          this.hideError();
        }
      }

      // Submit the property only when this list is showing and a breed is chosen. That keeps blank
      // "Dog Breed:" lines off the cart, and a hidden dog breed off a cat order.
      syncSubmit() {
        if (this.hiddenInput) this.hiddenInput.disabled = !this.active || !this.value;
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
        this.classList.add('dbp--invalid');
      }

      hideError() {
        if (!this.error) return;
        this.error.hidden = true;
        this.error.textContent = '';
        this.classList.remove('dbp--invalid');
      }
    }
  );
}

if (!customElements.get('pet-type-toggle')) {
  customElements.define(
    'pet-type-toggle',
    class PetTypeToggle extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';
        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"][data-pet]')) this.show(event.target.dataset.pet);
        });
        const checked = this.querySelector('input[type="radio"][data-pet]:checked');
        if (checked) this.show(checked.dataset.pet);
      }

      show(pet) {
        this.querySelectorAll('dog-breed-picker[data-pet]').forEach((picker) => {
          const on = picker.dataset.pet === pet;
          if (typeof picker.setActive === 'function') {
            picker.setActive(on);
          } else {
            picker.hidden = !on;
            picker.toggleAttribute('data-inactive', !on);
            const select = picker.querySelector('select');
            if (select) select.disabled = !on;
          }
        });
      }
    }
  );
}
